/**
 * Step 2 of the upload wizard (T4).
 *
 * Before T4 this step rendered native ``<select>`` / ``<select multiple>``
 * over an *eagerly* fetched list of every client + every employee. That
 * pattern broke once the registries grew past ~50 rows (no scrollable
 * combobox, no search-by-alias) — see the «Загрузка встречи» scope notes.
 *
 * The new shape:
 *
 *   • Client + Participants  → ``<Combobox<...> />`` with async search.
 *   • Meeting type           → plain ``<select>`` (closed enum, no search).
 *   • Inline-create          → footer action on the combobox opens the
 *                              corresponding existing FormDialog (no
 *                              mini-CRUD inside the wizard).
 *   • «Возможно, имеется в виду» → implicit: the combobox already shows
 *                              fuzzy matches by name+aliases above the
 *                              «+ Создать…» action. Operator picks from
 *                              the list or explicitly creates new.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { listClientsFull, listEmployeesFull } from "@/api/lookups";
import type {
  ClientResponse,
  ClientSummary,
  EmployeeResponse,
  EmployeeSummary,
  MeetingTypeSummary,
  UUID,
} from "@/api/types";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import { FolderPicker } from "@/components/folder/FolderPicker";
import { ClientFormDialog } from "@/components/registries/ClientFormDialog";
import { EmployeeFormDialog } from "@/components/registries/EmployeeFormDialog";

const metadataSchema = z.object({
  client_id: z.string().min(1),
  meeting_type_id: z.string().min(1),
  participants: z.array(z.string()).min(1),
  force_new: z.boolean().optional(),
  /**
   * folder-management-v3 §2.3 — required for non-admin callers. We keep
   * the schema tolerant (string, possibly empty) because admin callers
   * may omit and rely on the BE's active-folder fallback; the BE is the
   * source of truth for the 400 on non-admin + missing. The FolderPicker
   * auto-defaults on mount when a list is available, so in practice the
   * field is rarely empty by the time the user reaches the Confirm step.
   */
  folder_id: z.string().optional(),
});

export type MetadataValues = z.infer<typeof metadataSchema>;

export interface UploadStepMetadataProps {
  meetingTypes: MeetingTypeSummary[];
  defaultValues?: Partial<MetadataValues>;
  /**
   * DOM id for the underlying ``<form>`` so the one-step dialog's external
   * «Загрузить» button can submit it via ``<button form=…>``.
   */
  formId?: string;
  /** Fires the validated field values (submit is triggered from outside). */
  onSubmit: (values: MetadataValues) => void;
  /** Mirrors react-hook-form validity up so the parent can gate its button. */
  onValidityChange?: (valid: boolean) => void;
}

function clientToOption(c: ClientSummary): ComboboxOption<ClientSummary> {
  const aliases = c.aliases ?? [];
  return {
    value: c.id,
    data: c,
    label: c.name,
    secondary: aliases.length > 0 ? aliases.join(", ") : undefined,
  };
}

function employeeToOption(e: EmployeeSummary): ComboboxOption<EmployeeSummary> {
  const aliases = e.aliases ?? [];
  return {
    value: e.id,
    data: e,
    label: e.full_name,
    secondary: aliases.length > 0 ? aliases.join(", ") : undefined,
  };
}

export function UploadStepMetadata({
  meetingTypes,
  defaultValues,
  formId,
  onSubmit,
  onValidityChange,
}: UploadStepMetadataProps) {
  const { t } = useTranslation();

  const [client, setClient] = useState<ComboboxOption<ClientSummary> | null>(
    null,
  );
  const [participants, setParticipants] = useState<
    ComboboxOption<EmployeeSummary>[]
  >([]);

  const [clientFormOpen, setClientFormOpen] = useState(false);
  const [clientFormSeed, setClientFormSeed] = useState("");
  const [employeeFormOpen, setEmployeeFormOpen] = useState(false);
  const [employeeFormSeed, setEmployeeFormSeed] = useState("");

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<MetadataValues>({
    resolver: zodResolver(metadataSchema),
    mode: "onChange",
    defaultValues: {
      client_id: defaultValues?.client_id ?? "",
      meeting_type_id: defaultValues?.meeting_type_id ?? "",
      participants: defaultValues?.participants ?? [],
      force_new: defaultValues?.force_new ?? false,
      folder_id: defaultValues?.folder_id ?? "",
    },
  });
  const folderId = watch("folder_id");

  // Lift validity so the dialog's external «Загрузить» button can gate on it.
  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  async function searchClients(
    q: string,
  ): Promise<ComboboxOption<ClientSummary>[]> {
    const rows = await listClientsFull({ search: q, limit: 20 });
    return rows.map((r) =>
      clientToOption({
        id: r.id,
        name: r.name,
        aliases: r.aliases,
      }),
    );
  }

  async function searchEmployees(
    q: string,
  ): Promise<ComboboxOption<EmployeeSummary>[]> {
    const rows = await listEmployeesFull({ search: q, limit: 20 });
    return rows.map((r) =>
      employeeToOption({
        id: r.id,
        full_name: r.full_name,
        aliases: r.aliases,
      }),
    );
  }

  function selectClient(next: ComboboxOption<ClientSummary> | null): void {
    setClient(next);
    setValue("client_id", next?.value ?? "", { shouldValidate: true });
  }

  function selectParticipants(next: ComboboxOption<EmployeeSummary>[]): void {
    setParticipants(next);
    setValue(
      "participants",
      next.map((o) => o.value),
      { shouldValidate: true },
    );
  }

  const submit = handleSubmit((values) => {
    onSubmit({
      ...values,
      participants: values.participants.filter(
        (id): id is UUID => id.length > 0,
      ),
    });
  });

  // ClientFormDialog returns a full ClientResponse; we narrow once and pass
  // it back into the combobox state.
  function handleClientSaved(saved: ClientSummary | ClientResponse): void {
    const summary: ClientSummary = {
      id: saved.id,
      name: saved.name,
      aliases:
        "aliases" in saved && Array.isArray(saved.aliases)
          ? saved.aliases
          : undefined,
    };
    selectClient(clientToOption(summary));
  }

  function handleEmployeeSaved(
    saved: EmployeeSummary | EmployeeResponse,
  ): void {
    const summary: EmployeeSummary = {
      id: saved.id,
      full_name: saved.full_name,
      aliases:
        "aliases" in saved && Array.isArray(saved.aliases)
          ? saved.aliases
          : undefined,
    };
    selectParticipants([...participants, employeeToOption(summary)]);
  }

  return (
    <form id={formId} onSubmit={submit} className="space-y-3">
      {/* hidden registered fields — Combobox / FolderPicker write via setValue */}
      <input type="hidden" {...register("client_id")} />
      <input type="hidden" {...register("participants")} />
      <input type="hidden" {...register("folder_id")} />

      <div className="space-y-1.5">
        <Label htmlFor="upload-folder">{t("folders.picker.label")}</Label>
        <FolderPicker
          id="upload-folder"
          value={folderId === undefined || folderId === "" ? null : folderId}
          onChange={(id) => setValue("folder_id", id, { shouldValidate: true })}
          ariaInvalid={errors.folder_id !== undefined}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="upload-client">{t("meetings.upload.client")}</Label>
        <Combobox<ClientSummary>
          id="upload-client"
          mode="single"
          value={client}
          onChange={selectClient}
          search={searchClients}
          placeholder={t("meetings.upload.clientPlaceholder")}
          emptyLabel={t("meetings.upload.comboboxEmpty")}
          ariaInvalid={errors.client_id !== undefined}
          footerAction={{
            label: t("meetings.upload.createClientInline"),
            onSelect: (q) => {
              setClientFormSeed(q);
              setClientFormOpen(true);
            },
          }}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="upload-type">{t("meetings.upload.meetingType")}</Label>
        <select
          id="upload-type"
          className="h-9 w-full rounded-md border bg-background px-2 text-sm"
          aria-invalid={errors.meeting_type_id ? true : undefined}
          {...register("meeting_type_id")}
        >
          <option value="">—</option>
          {meetingTypes.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="upload-participants">
          {t("meetings.upload.participants")}
        </Label>
        <Combobox<EmployeeSummary>
          id="upload-participants"
          mode="multi"
          value={participants}
          onChange={selectParticipants}
          search={searchEmployees}
          placeholder={t("meetings.upload.participantsPlaceholder")}
          emptyLabel={t("meetings.upload.comboboxEmpty")}
          ariaInvalid={errors.participants !== undefined}
          footerAction={{
            label: t("meetings.upload.createEmployeeInline"),
            onSelect: (q) => {
              setEmployeeFormSeed(q);
              setEmployeeFormOpen(true);
            },
          }}
        />
        <p className="text-xs text-muted-foreground">
          {t("meetings.upload.participantsHint")}
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" {...register("force_new")} />
        {t("meetings.upload.forceNew")}
      </label>

      <ClientFormDialog
        open={clientFormOpen}
        existing={null}
        initialName={clientFormSeed}
        onSaved={handleClientSaved}
        onClose={() => setClientFormOpen(false)}
      />
      <EmployeeFormDialog
        open={employeeFormOpen}
        existing={null}
        initialName={employeeFormSeed}
        onSaved={handleEmployeeSaved}
        onClose={() => setEmployeeFormOpen(false)}
      />
    </form>
  );
}
